/**
 * Owner: Masa Bayu
 * File: QxStudioImportGate.tsx
 * Date:Time: 2026-10-29 12:40
 * Description: Gate Import PDF pada permukaan Studio — busy atau ralat decode nyata.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { importPageFraction } from '@qx/qxwriter-app/lib/qx-writer-import-fraction';

export function QxStudioImportGate({
  hint,
  failed = false,
  onDismiss,
}: {
  readonly hint: string;
  readonly failed?: boolean | undefined;
  readonly onDismiss?: (() => void) | undefined;
}): ReactElement {
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    if (failed) return undefined;
    const started = Date.now();
    const tick = window.setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - started) / 1000));
    }, 250);
    return () => window.clearInterval(tick);
  }, [failed]);
  const elapsedLabel =
    elapsedSec < 60 ? `${elapsedSec}s` : `${Math.floor(elapsedSec / 60)}m ${elapsedSec % 60}s`;
  const fraction = importPageFraction(hint);
  const opened = fraction === null ? 0 : fraction;
  return createPortal(
    <div
      className="qx-studio-import-gate"
      role={failed ? 'alertdialog' : 'status'}
      aria-busy={failed ? undefined : 'true'}
      aria-live="assertive"
      data-qx-studio-import-gate=""
      data-qx-studio-import-failed={failed ? 'true' : undefined}
    >
      <div className="qx-studio-import-gate__card">
        <p className="qx-studio-import-gate__title">
          {failed ? 'Import failed' : 'Importing PDF…'}
        </p>
        <p className="qx-studio-import-gate__hint">{hint}</p>
        {failed ? null : (
          <>
            <span
              className="qx-studio-import-gate__track"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(opened * 100)}
              aria-valuetext={hint}
            >
              <span className="qx-studio-import-gate__fill" style={{ width: `${opened * 100}%` }} />
            </span>
            <p className="qx-studio-import-gate__time">{elapsedLabel}</p>
          </>
        )}
        {failed && onDismiss ? (
          <button
            type="button"
            className="qx-studio-import-gate__dismiss"
            onClick={onDismiss}
            data-qx-studio-import-dismiss=""
          >
            Dismiss
          </button>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
